'use client';

import { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import styles from './admin.module.css';

const GCC_NATIONS = [
  'UAE',
  'Saudi Arabia',
  'Qatar',
  'Oman',
  'Bahrain',
  'Kuwait',
  'India'
];

const NATION_TO_CODE = {
  'UAE': 'AE',
  'Saudi Arabia': 'SA',
  'Qatar': 'QA',
  'Oman': 'OM',
  'Bahrain': 'BH',
  'Kuwait': 'KW',
  'India': 'IN'
};

export default function AdminDashboard() {
  // Login State
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [level, setLevel] = useState('Nation');
  const [loginNationFilter, setLoginNationFilter] = useState(''); // New state for Nation -> Classroom drilldown
  const [selectedValue, setSelectedValue] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Data State
  const [classroomsData, setClassroomsData] = useState([]);
  const [registrations, setRegistrations] = useState([]);

  // Dashboard Filters
  const [studentTypeFilter, setStudentTypeFilter] = useState('All'); // 'All', 'Hadiya', 'Teen Hadiya', 'Suffa'
  const [classroomFilter, setClassroomFilter] = useState('All');
  const [viewMode, setViewMode] = useState('All'); // 'All', 'NationSummary', 'ClassroomSummary'
  const [summaryNationFilter, setSummaryNationFilter] = useState('All');

  const BASE_PATH = '/suffa_dars';

  // Fetch classrooms on mount for the login dropdown
  useEffect(() => {
    fetch(`${BASE_PATH}/api/classrooms`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setClassroomsData(data.classrooms || []);
        }
      })
      .catch(err => console.error('Failed to fetch classrooms:', err));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    if (level !== 'International' && !selectedValue) {
      setLoginError(`Please select a ${level}`);
      return;
    }

    setIsLoading(true);

    try {
      const res = await fetch(`${BASE_PATH}/api/admin/registrations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ level, selectedValue: level === 'International' ? 'International' : selectedValue, password })
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setRegistrations(data.data);
        setIsAuthenticated(true);
      } else {
        setLoginError(data.error || 'Authentication failed');
      }
    } catch (err) {
      setLoginError('Failed to connect to server');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setRegistrations([]);
    setPassword('');
    setStudentTypeFilter('All');
    setClassroomFilter('All');
    setViewMode('All');
    setSummaryNationFilter('All');
  };

  // Filtered registrations for the table
  const filteredRegistrations = useMemo(() => {
    return registrations.filter(reg => {
      // 1. Student Type Filter
      if (studentTypeFilter === 'Hadiya' && reg.is_hadiya_student !== 1) return false;
      if (studentTypeFilter === 'Teen Hadiya' && reg.is_hadiya_student !== 2) return false;
      if (studentTypeFilter === 'Suffa' && reg.is_hadiya_student !== 0) return false;

      // 2. Classroom Filter (only applies if logged in as Nation)
      if (level === 'Nation' && classroomFilter !== 'All') {
        if (reg.classroom !== classroomFilter) return false;
      }

      return true;
    });
  }, [registrations, studentTypeFilter, classroomFilter, level]);

  // Unique classrooms in the current dataset (for the Nation-level classroom filter)
  const availableClassroomsForFilter = useMemo(() => {
    if (level !== 'Nation') return [];
    const rooms = new Set(registrations.map(r => r.classroom).filter(Boolean));
    return Array.from(rooms).sort();
  }, [registrations, level]);

  const handleDownloadExcel = () => {
    if (filteredRegistrations.length === 0) return;

    let data = [];

    if (level === 'International' && viewMode === 'NationSummary') {
      const counts = {};
      filteredRegistrations.forEach(reg => {
        const nat = reg.nation || 'Unknown';
        counts[nat] = (counts[nat] || 0) + 1;
      });
      const rows = Object.entries(counts).sort((a,b) => b[1] - a[1]);
      data = rows.map(([nat, count]) => ({
        'Nation': nat,
        'Total Registrations': count
      }));
    } else if (level === 'International' && viewMode === 'ClassroomSummary') {
      let regs = filteredRegistrations;
      if (summaryNationFilter !== 'All') {
        regs = regs.filter(r => r.nation === summaryNationFilter);
      }
      const counts = {};
      regs.forEach(reg => {
        const cls = reg.place || reg.classroom || 'Unknown';
        const key = `${reg.nation || 'Unknown'} - ${cls}`;
        if (!counts[key]) counts[key] = { nation: reg.nation || 'Unknown', classroom: cls, count: 0 };
        counts[key].count++;
      });
      const rows = Object.values(counts).sort((a,b) => b.count - a.count);
      data = rows.map(r => ({
        'Nation': r.nation,
        'Classroom / Place': r.classroom,
        'Total Registrations': r.count
      }));
    } else {
      data = filteredRegistrations.map((reg, i) => {
        let type = 'Suffa';
        if (reg.is_hadiya_student === 1) type = 'Hadiya';
        else if (reg.is_hadiya_student === 2) type = 'Teen Hadiya';

        let roll = reg.suffa_roll_no || '';
        if (reg.hadiya_roll_no) {
          roll += ` (Hadiya: ${reg.hadiya_roll_no})`;
        }

        const placeClass = reg.place || reg.classroom || '';

        return {
          'S.No': i + 1,
          'Roll No': roll,
          'Name': reg.name || '',
          'Type': type,
          'Mobile': reg.mobile_number || '',
          'WhatsApp': reg.whatsapp_number || '',
          'Nation': reg.nation || '',
          'Classroom / Place': placeClass,
          'Date': new Date(reg.created_at).toLocaleDateString()
        };
      });
    }

    if (data.length === 0) return;

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Registrations");
    
    let filename = 'Suffa_Registrations';
    if (level === 'International' && viewMode === 'NationSummary') filename = 'Nation_Summary';
    if (level === 'International' && viewMode === 'ClassroomSummary') filename = 'Classroom_Summary';
    
    XLSX.writeFile(workbook, `${filename}_${new Date().getTime()}.xlsx`);
  };

  const renderNationSummary = () => {
    const counts = {};
    filteredRegistrations.forEach(reg => {
      const nat = reg.nation || 'Unknown';
      counts[nat] = (counts[nat] || 0) + 1;
    });

    const rows = Object.entries(counts).sort((a,b) => b[1] - a[1]);

    return (
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>Nation</th>
              <th className={styles.th}>Total Registrations</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([nat, count], idx) => (
              <tr key={idx} className={styles.tr}>
                <td className={styles.td}>{nat}</td>
                <td className={styles.td}><strong>{count}</strong></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderClassroomSummary = () => {
    let regs = filteredRegistrations;
    if (summaryNationFilter !== 'All') {
      regs = regs.filter(r => r.nation === summaryNationFilter);
    }

    const counts = {};
    regs.forEach(reg => {
      const cls = reg.place || reg.classroom || 'Unknown';
      const key = `${reg.nation || 'Unknown'} - ${cls}`;
      if (!counts[key]) counts[key] = { nation: reg.nation || 'Unknown', classroom: cls, count: 0 };
      counts[key].count++;
    });

    const rows = Object.values(counts).sort((a,b) => b.count - a.count);

    return (
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>Nation</th>
              <th className={styles.th}>Classroom / Place</th>
              <th className={styles.th}>Total Registrations</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx} className={styles.tr}>
                <td className={styles.td}>{r.nation}</td>
                <td className={styles.td}>{r.classroom}</td>
                <td className={styles.td}><strong>{r.count}</strong></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className={styles.container}>
        <div className={styles.loginCard}>
          <h1 className={styles.title}>Admin Login</h1>
          
          {loginError && <div className={styles.error}>{loginError}</div>}
          
          <form onSubmit={handleLogin}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Access Level</label>
              <select 
                className={styles.select}
                value={level} 
                onChange={(e) => {
                  setLevel(e.target.value);
                  setSelectedValue('');
                  setLoginNationFilter('');
                }}
              >
                <option value="Nation">Nation</option>
                <option value="Classroom">Classroom</option>
                <option value="International">International</option>
              </select>
            </div>

            {level === 'Classroom' && (
              <div className={styles.formGroup}>
                <label className={styles.label}>First, Select Nation</label>
                <select 
                  className={styles.select}
                  value={loginNationFilter} 
                  onChange={(e) => {
                    setLoginNationFilter(e.target.value);
                    setSelectedValue(''); // Reset classroom when nation changes
                  }}
                >
                  <option value="">-- Select Nation --</option>
                  {GCC_NATIONS.map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            )}

            {(level === 'Nation' || (level === 'Classroom' && loginNationFilter)) && (
              <div className={styles.formGroup}>
                <label className={styles.label}>Select {level}</label>
                <select 
                  className={styles.select}
                  value={selectedValue} 
                  onChange={(e) => setSelectedValue(e.target.value)}
                >
                  <option value="">-- Select {level} --</option>
                  {level === 'Nation' && GCC_NATIONS.map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                  {level === 'Classroom' && classroomsData
                    .filter(c => c.country_code === NATION_TO_CODE[loginNationFilter])
                    .map(c => (
                      <option key={c.id} value={c.description}>{c.description}</option>
                  ))}
                </select>
              </div>
            )}

            <div className={styles.formGroup}>
              <label className={styles.label}>Password</label>
              <input 
                type="password" 
                className={styles.input}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                required
              />
            </div>

            <button type="submit" className={styles.button} disabled={isLoading}>
              {isLoading ? <span className={styles.spinner}></span> : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.dashboardHeader}>
        <div>
          <h1 className={styles.dashboardTitle}>Suffa Dars Registrations</h1>
          <div className={styles.dashboardMeta}>
            <span className={styles.metaItem}>
              Scope: <span className={styles.metaValue}>{level} ({selectedValue})</span>
            </span>
            <span className={styles.metaItem}>
              Total Records: <span className={styles.metaValue}>{filteredRegistrations.length}</span>
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={handleDownloadExcel} className={styles.logoutBtn} style={{ background: '#10b981', color: 'white' }}>Download Excel File</button>
          <button onClick={handleLogout} className={styles.logoutBtn}>Logout</button>
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Type:</label>
          <select 
            className={styles.filterSelect}
            value={studentTypeFilter}
            onChange={(e) => setStudentTypeFilter(e.target.value)}
          >
            <option value="All">All Students</option>
            <option value="Hadiya">Hadiya Only</option>
            <option value="Teen Hadiya">Teen Hadiya Only</option>
            <option value="Suffa">Suffa Only</option>
          </select>
        </div>

        {level === 'International' && (
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>View Mode:</label>
            <select 
              className={styles.filterSelect}
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
            >
              <option value="All">All Records</option>
              <option value="NationSummary">Nation Wise</option>
              <option value="ClassroomSummary">Classroom Wise</option>
            </select>
          </div>
        )}

        {(level === 'International' && viewMode === 'ClassroomSummary') && (
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Nation:</label>
            <select 
              className={styles.filterSelect}
              value={summaryNationFilter}
              onChange={(e) => setSummaryNationFilter(e.target.value)}
            >
              <option value="All">All Nations</option>
              {GCC_NATIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        )}

        {level === 'Nation' && selectedValue !== 'India' && (
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Classroom:</label>
            <select 
              className={styles.filterSelect}
              value={classroomFilter}
              onChange={(e) => setClassroomFilter(e.target.value)}
            >
              <option value="All">All Classrooms</option>
              {availableClassroomsForFilter.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {level === 'International' && viewMode === 'NationSummary' ? renderNationSummary() :
       level === 'International' && viewMode === 'ClassroomSummary' ? renderClassroomSummary() : (
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>S.No</th>
              <th className={styles.th}>Roll No</th>
              <th className={styles.th}>Name</th>
              <th className={styles.th}>Type</th>
              <th className={styles.th}>Mobile</th>
              <th className={styles.th}>WhatsApp</th>
              <th className={styles.th}>Nation</th>
              <th className={styles.th}>Classroom / Place</th>
              <th className={styles.th}>Date</th>
            </tr>
          </thead>
          <tbody>
            {filteredRegistrations.length === 0 ? (
              <tr>
                <td colSpan="9" className={styles.emptyState}>No registrations found.</td>
              </tr>
            ) : (
              filteredRegistrations.map((reg, index) => (
                <tr key={reg.id} className={styles.tr}>
                  <td className={styles.td}>{index + 1}</td>
                  <td className={styles.td}>{reg.suffa_roll_no}</td>
                  <td className={styles.td}>
                    <strong>{reg.name}</strong>
                    {reg.hadiya_roll_no && <div style={{fontSize: '0.75rem', color: '#64748b'}}>Hadiya: {reg.hadiya_roll_no}</div>}
                  </td>
                  <td className={styles.td}>
                    {reg.is_hadiya_student === 1 && (
                      <span className={`${styles.badge} ${styles.badgeHadiya}`}>Hadiya</span>
                    )}
                    {reg.is_hadiya_student === 2 && (
                      <span className={`${styles.badge} ${styles.badgeHadiya}`} style={{background: '#8b5cf6', color: '#fff'}}>Teen Hadiya</span>
                    )}
                    {reg.is_hadiya_student === 0 && (
                      <span className={`${styles.badge} ${styles.badgeSuffa}`}>Suffa</span>
                    )}
                  </td>
                  <td className={styles.td}>{reg.mobile_number}</td>
                  <td className={styles.td}>{reg.whatsapp_number}</td>
                  <td className={styles.td}>{reg.nation}</td>
                  <td className={styles.td}>
                    {reg.place || reg.classroom || ''}
                  </td>
                  <td className={styles.td}>
                    {new Date(reg.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}
